import { collection, doc, addDoc, deleteDoc, getDocs, query, where, Timestamp, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase.ts';
import { getUserById, getDisplayName, getUserByFirebaseUid } from './userDatabase.ts';
import { User } from '../models/User';

interface MeetingAttendee {
  id: string;
  meetingId: string;
  userId: string;  // This must be a numeric ID from the users table
  userName: string;
  attendanceType: 'in-person' | 'virtual' | 'not-attending';
  attendedAt: Timestamp;
}

// Helper function to get the correct numeric user ID
const getNumericUserId = async (user: User): Promise<string> => {
  // If it's already a numeric ID, use it
  if (/^\d+$/.test(user.id)) {
    return user.id;
  }
  
  // If it's a Firebase Auth ID, try to get the numeric ID
  try {
    const numericUser = await getUserByFirebaseUid(user.id);
    if (numericUser && /^\d+$/.test(numericUser.id)) {
      return numericUser.id;
    }
  } catch (error) {
    console.error('Failed to get numeric user ID:', error);
  }
  
  // If we can't find a numeric ID, this is an error
  throw new Error(`User ${user.fullName || user.email} does not have a valid numeric ID: ${user.id}`);
};

export const addMeetingAttendee = async (meetingId: string, user: User, attendanceType: 'in-person' | 'virtual' | 'not-attending' = 'in-person'): Promise<void> => {
  const attendeesRef = collection(db, 'meetingAttendees');
  const numericUserId = await getNumericUserId(user);
  
  await addDoc(attendeesRef, {
    meetingId,
    userId: numericUserId,  // Always use numeric ID
    userName: user.fullName || 'Unknown User',
    attendanceType,
    attendedAt: Timestamp.fromDate(new Date())
  });
};

export const updateMeetingAttendee = async (meetingId: string, user: User, attendanceType: 'in-person' | 'virtual' | 'not-attending'): Promise<void> => {
  const numericUserId = await getNumericUserId(user);

  // Check if user already has an attendance record
  const attendeesRef = collection(db, 'meetingAttendees');
  const q = query(attendeesRef, 
    where('meetingId', '==', meetingId),
    where('userId', '==', numericUserId)  // Always use numeric ID
  );
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    // Add new attendance record
    await addMeetingAttendee(meetingId, user, attendanceType);
  } else {
    // Update existing attendance record
    const docRef = querySnapshot.docs[0].ref;
    await updateDoc(docRef, {
      attendanceType,
      userName: user.fullName || 'Unknown User',
      attendedAt: Timestamp.fromDate(new Date())
    });
  }
};

export const removeMeetingAttendee = async (meetingId: string, userId: string): Promise<void> => {
  // Ensure we're using a numeric ID
  if (!/^\d+$/.test(userId)) {
    console.error('Attempting to remove attendee with non-numeric ID:', userId);
    return;
  }
  
  const attendeesRef = collection(db, 'meetingAttendees');
  const q = query(attendeesRef, 
    where('meetingId', '==', meetingId),
    where('userId', '==', userId)  // Always use numeric ID
  );
  const querySnapshot = await getDocs(q);
  
  querySnapshot.forEach(async (doc) => {
    await deleteDoc(doc.ref);
  });
};

export const getAttendeesForMeeting = async (meetingId: string): Promise<MeetingAttendee[]> => {
  const attendeesRef = collection(db, 'meetingAttendees');
  const q = query(attendeesRef, where('meetingId', '==', meetingId));
  const querySnapshot = await getDocs(q);
  
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as MeetingAttendee));
};

export const hasUserAttendedMeeting = async (meetingId: string, userId: string): Promise<boolean> => {
  if (!meetingId || !userId) return false;
  
  try {
    // If it's a Firebase Auth ID, convert it to numeric ID
    let numericUserId = userId;
    if (!/^\d+$/.test(userId)) {
      const user = await getUserByFirebaseUid(userId);
      if (!user) {
        console.error('User not found for Firebase Auth ID:', userId);
        return false;
      }
      numericUserId = user.id;
    }
    
    const attendeesRef = collection(db, 'meetingAttendees');
    const q = query(attendeesRef, 
      where('meetingId', '==', meetingId),
      where('userId', '==', numericUserId)  // Always use numeric ID
    );
    const querySnapshot = await getDocs(q);
    
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking meeting attendance:', error);
    return false;
  }
};

export const getUserAttendanceType = async (meetingId: string, userId: string): Promise<'in-person' | 'virtual' | 'not-attending' | null> => {
  if (!meetingId || !userId) return null;
  
  try {
    // If it's a Firebase Auth ID, convert it to numeric ID
    let numericUserId = userId;
    if (!/^\d+$/.test(userId)) {
      const user = await getUserByFirebaseUid(userId);
      if (!user) {
        console.error('User not found for Firebase Auth ID:', userId);
        return null;
      }
      numericUserId = user.id;
    }
    
    const attendeesRef = collection(db, 'meetingAttendees');
    const q = query(attendeesRef, 
      where('meetingId', '==', meetingId),
      where('userId', '==', numericUserId)  // Always use numeric ID
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const attendeeData = querySnapshot.docs[0].data();
    return attendeeData.attendanceType || 'in-person';
  } catch (error) {
    console.error('Error getting user attendance type:', error);
    return null;
  }
};

// Returns the number of confirmed attendees (in-person or virtual) for a meeting
export const getConfirmedAttendeeCount = async (meetingId: string): Promise<number> => {
  const attendees = await getAttendeesForMeeting(meetingId);
  return attendees.filter(a => a.attendanceType === 'in-person' || a.attendanceType === 'virtual').length;
}; 